import { useSearchParams } from 'react-router-dom';

const PURCHASE_MODEL_STORAGE_KEY = 'purchase_model';
const PURCHASE_MODEL_COOKIE_NAME = 'pm';
const PURCHASE_MODEL_DIRECT = 'direct';
const COOKIE_EXPIRY_DAYS = 90;

function setCookie(name: string, value: string, days: number) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  // Use .catchbio.com domain in production for cross-subdomain sharing (www ↔ app)
  const domainAttr = window.location.hostname.endsWith('.catchbio.com') ? ';domain=.catchbio.com' : '';
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax${domainAttr}`;
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Resolves the current purchase model from URL params, localStorage, or cookie.
 * Sticky: once a user is tagged as 'direct' via ?pm=direct, they stay in
 * the direct flow on return visits even without the param.
 */
export function getPurchaseModel(searchParams?: URLSearchParams): string {
  const urlParams = searchParams ?? new URLSearchParams(window.location.search);
  const pmParam = urlParams.get('pm');

  if (pmParam === PURCHASE_MODEL_DIRECT) {
    localStorage.setItem(PURCHASE_MODEL_STORAGE_KEY, PURCHASE_MODEL_DIRECT);
    setCookie(PURCHASE_MODEL_COOKIE_NAME, PURCHASE_MODEL_DIRECT, COOKIE_EXPIRY_DAYS);
    return PURCHASE_MODEL_DIRECT;
  }

  // Check localStorage (primary sticky store)
  const stored = localStorage.getItem(PURCHASE_MODEL_STORAGE_KEY);
  if (stored === PURCHASE_MODEL_DIRECT) {
    return PURCHASE_MODEL_DIRECT;
  }

  // Check cookie (fallback for cross-subdomain or cleared localStorage)
  const cookieValue = getCookie(PURCHASE_MODEL_COOKIE_NAME);
  if (cookieValue === PURCHASE_MODEL_DIRECT) {
    localStorage.setItem(PURCHASE_MODEL_STORAGE_KEY, PURCHASE_MODEL_DIRECT);
    return PURCHASE_MODEL_DIRECT;
  }

  return 'trial';
}

/**
 * React hook that resolves the purchase model from URL, localStorage, or cookie.
 * Uses useSearchParams internally for router integration.
 */
export function usePurchaseModel(): { purchaseModel: string; isTrial: boolean } {
  const [searchParams] = useSearchParams();
  const model = getPurchaseModel(searchParams);
  return { purchaseModel: model, isTrial: model !== PURCHASE_MODEL_DIRECT };
}
